import React, { useState, useEffect } from "react";
import { InputOTP } from "antd-input-otp";
import { message, Tooltip } from "antd";
import { checkOtp, forgetPassword } from "../../api";
import { getSubdomain } from "utils";
import { useTranslation } from "react-i18next";
import { changeLanguage } from "utils";
import LoginBrandLogo from "modules/auth/components/login/LoginBrandLogo";

const OTP = ({ setPage, setData, data }) => {
  const { t, i18n } = useTranslation();
  const [otp, setOtp] = useState("");
  const [timeLeft, setTimeLeft] = useState(120);
  const subdomain = getSubdomain();
  const isDark = +localStorage.getItem("isDark");

  useEffect(() => {
    if (timeLeft <= 0) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [timeLeft]);

  const handleModeClick = () => {
    localStorage.setItem("isDark", isDark ? 0 : 1);
    window.location.reload();
  };

  const changeLng = () =>
    changeLanguage(i18n, i18n.language === "ar" ? "en" : "ar");

  const formatTime = (seconds) => {
    const m = String(Math.floor(seconds / 60)).padStart(2, "0");
    const s = String(seconds % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  const handleChange = (value) => {
    setOtp(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const code = otp.join("");
    const updatedData = { ...data, code, slug: subdomain };

    try {
      const res = await checkOtp(updatedData);
      if (res.status) {
        setPage(3);
        setData(updatedData);
      } else {
        message.error(t(res.data) || res.message || t("somethingWentWrong"));
      }
    } catch (error) {
      message.error(t("somethingWentWrong"));
    }
  };

  const handleResend = async () => {
    if (timeLeft > 0) return;
    try {
      const res = await forgetPassword({
        mobile: data.mobile,
        slug: subdomain,
      });
      if (res.status) {
        setTimeLeft(120);
        message.success(t("code_sent"));
      } else {
        message.error(res.message || t("somethingWentWrong"));
      }
    } catch {
      message.error(t("somethingWentWrong"));
    }
  };

  return (
    <section className="flex flex-col lg:flex-row items-stretch relative min-h-screen h-auto lg:h-screen">
      <div className="absolute top-[32px] z-[60] end-[16px] lg:end-[calc(48%+16px)] inline-flex items-center gap-2">
        <div
          className={`
            w-9 h-9 p-1 rounded-[20px] border
            inline-flex justify-center items-center cursor-pointer
            ${isDark ? "bg-slate-900 border-slate-800" : "bg-surface border-border-light"}
          `}
          onClick={handleModeClick}
        >
          <img
            src={isDark ? "/icons/sun.svg" : "/icons/moon.svg"}
            alt="toggle mode"
          />
        </div>
        <Tooltip title={i18n.language === "ar" ? "English" : "عربي"}>
          <div
            className={`
            w-9 h-9 p-1 rounded-[20px] border
            inline-flex justify-center items-center cursor-pointer
            ${isDark ? "bg-slate-900 border-slate-800" : "bg-surface border-border-light"}
          `}
            onClick={changeLng}
          >
            <img
              src={
                isDark ? "/icons/mlti-lang-dark.svg" : "/icons/mlti-lang.svg"
              }
              alt="toggle language"
              className="w-5 h-5"
            />
          </div>
        </Tooltip>
      </div>

      <LoginBrandLogo isDark={isDark} />
      <div
        className={`w-full lg:w-[52%] flex flex-col justify-center px-4 sm:px-8 lg:px-[72px] py-8 lg:py-0 lg:h-full pb-[70px] ${
          isDark ? "bg-chrome" : "bg-surface"
        }`}
      >
        <h1
          className={`text-xl sm:text-2xl lg:text-[24px] font-bold mt-6 mb-2.5 leading-6 sm:leading-8 ${
            isDark ? "login-text-color" : "text-brand"
          }`}
        >
          {t("login.code")}
        </h1>

        <span
          className={`text-sm inline-block ${
            isDark ? "text-[#B4B4B4]" : "text-[#737373]"
          }`}
        >
          {t("login.code_sent_to")}{" "}
          <span className="inline-block" dir="ltr">
            {"*".repeat(Math.max(0, (data?.mobile || "").length - 4))}
            {(data?.mobile || "").slice(-4)}
          </span>
        </span>

        <form className="mt-8" onSubmit={handleSubmit}>
          <div className="flex justify-center mt-4">
            <InputOTP
              value={otp}
              onChange={handleChange}
              length={6}
              type="number"
              autoFocus
              inputClassName={`h-[32px] border rounded-lg text-center text-[20px] font-semibold focus:outline-none focus:ring-2 focus:ring-primary bg-transparent ${
                isDark
                  ? "border-[#f6f6f699] text-[#fff]"
                  : "border-[#E0E0E0] text-[#181c32]"
              }`}
            />
          </div>
          <button
            type="submit"
            className="bg-brand hover:bg-brand/90 rounded-lg flex items-center justify-center w-full h-[48px] sm:h-[56px] text-surface font-semibold mt-8 text-sm sm:text-base duration-200"
          >
            {t("ok")}
          </button>
          <button
            type="button"
            disabled={timeLeft > 0}
            onClick={handleResend}
            className={`rounded-lg flex items-center justify-center w-full h-[48px] sm:h-[56px] font-semibold mt-[14px] text-sm sm:text-base border duration-200 ${
              timeLeft > 0 ? "opacity-50 cursor-not-allowed" : ""
            } ${
              isDark
                ? "bg-transparent hover:bg-surface/10 text-[#F6F6F6] border-[#F6F6F6]"
                : "bg-transparent hover:bg-gray-100 text-[#737373] border-[#E0E0E0]"
            }`}
          >
            {t("login.again")}
          </button>

          <span
            className={`text-sm mt-8 flex justify-center ${
              isDark ? "text-[#B4B4B4]" : "text-[#737373]"
            }`}
          >
            {formatTime(timeLeft)} {t("login.send_code")}
          </span>
        </form>
      </div>

      <div
        className="relative w-full lg:w-[48%] min-h-[400px] lg:h-full bg-no-repeat bg-center order-first lg:order-last login-bg-image"
        style={{
          backgroundImage: "url('/login-img.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="lg:hidden absolute inset-0 z-50 flex items-center justify-center"></div>
      </div>
    </section>
  );
};

export default OTP;
